// AbSyncBar — "Synchronisieren"-Button + "Letzter Sync"-Chip im Kopf der AbShell (Vorbild
// OC-Pilot sync/SyncContext.tsx SyncButton, hier ohne eigenen Provider da nur EIN Verbraucher).
// GET /mail/status und POST /mail/sync sind serverseitig ADMIN-only (Betriebsinfo/Zugangsdaten) —
// bewusst wie im bisherigen Code nur fuer Admins sichtbar (RBAC, kein Mail-aus-Degradierungsfall).
// mail/status wird alle 60s gepollt (Bauplan), damit auch automatische Laeufe sichtbar werden.

import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Mail } from 'lucide-react';
import { relativeTimeParts } from '../ui';
import { mailStatus, mailSync } from '../../api';

const POLL_MS = 60000;

const AbSyncBar = ({ user, isDarkMode, t, showSuccess, showError }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    mailStatus(user).then(setInfo).catch(() => setInfo(null));
  }, [user]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const runSync = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await mailSync(user);
      showSuccess?.(t('ab.mailSyncStarted'));
      if (res && !res.alreadyRunning) load();
    } catch (e) {
      // 409 = deaktiviert oder laeuft bereits — als Hinweis toasten statt hart als Fehler.
      showError?.(e.status === 409 ? t('ab.shell.syncDisabledInfo') : e.message);
    } finally {
      setBusy(false);
      load();
    }
  };

  const chipBase = 'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap';
  const chipOk = isDarkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-50 text-emerald-600';
  const chipErr = isDarkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-600';
  const chipIdle = isDarkMode ? 'bg-white/10 text-white/40' : 'bg-slate-100 text-slate-400';

  const lastRun = info?.lastRun;
  const rt = relativeTimeParts(lastRun?.finishedAt);
  const whenText = !rt
    ? null
    : rt.unit === 'now'
      ? t('ab.shell.timeJustNow')
      : t(`ab.shell.time${rt.unit === 'min' ? 'Min' : rt.unit === 'hours' ? 'Hours' : 'Days'}Ago`, { n: rt.n });

  const chip = (() => {
    if (!info) return null;
    if (!lastRun) return <span className={`${chipBase} ${chipIdle}`}>{t('ab.shell.syncNoRunYet')}</span>;
    if (lastRun.error) {
      return (
        <span className={`${chipBase} ${chipErr}`} title={lastRun.error || ''}>
          {t('ab.shell.syncLastRun', { when: whenText || '—' })} · {t('ab.shell.syncFailed')}
        </span>
      );
    }
    return (
      <span
        className={`${chipBase} ${chipOk}`}
        title={t('ab.mailLastRunTooltip', {
          finishedAt: lastRun.finishedAt ? new Date(lastRun.finishedAt).toLocaleString('de-DE') : '—',
          found: lastRun.found ?? 0, processed: lastRun.processed ?? 0,
          ignored: lastRun.ignored ?? 0, failed: lastRun.failed ?? 0,
        })}
      >
        {t('ab.shell.syncLastRun', { when: whenText || '—' })} · {t('ab.shell.syncNewCount', { n: lastRun.processed ?? 0 })}
      </span>
    );
  })();

  return (
    <div className="flex items-center gap-3 flex-wrap min-w-0">
      <button
        type="button"
        onClick={runSync}
        disabled={busy || info?.running}
        title={info?.enabled === false ? t('ab.mailDisabledHint') : t('ab.shell.syncButton')}
        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode
          ? 'bg-white/10 hover:bg-white/15 text-white/80 border border-white/10'
          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
      >
        {info?.enabled === false ? <Mail className="w-4 h-4" /> : <RefreshCw className={`w-4 h-4 ${busy || info?.running ? 'animate-spin' : ''}`} />}
        {busy || info?.running ? t('ab.shell.syncBusy') : t('ab.shell.syncButton')}
      </button>
      {chip}
    </div>
  );
};

export default AbSyncBar;
